import Chat, { DayHeaderFormat, MessageTimestampFormat } from 'devextreme-react/chat';
import type { MessageDeletedEvent, MessageEnteredEvent } from '@jarvis/platform-file-review';
import LoadIndicator from 'devextreme-react/load-indicator';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { showErrorToast } from '../notifications/toast';
import type { DiscussionMessage } from './types';

export interface FeedbackDiscussionChatProps {
  messages: DiscussionMessage[];
  currentUserId: string;
  currentUserName: string;
  loading?: boolean;
  readOnly?: boolean;
  title?: string;
  onMessageEntered: (text: string) => void | Promise<void>;
  onMessageDeleted?: (messageId: string) => void | Promise<void>;
}

type ChatItem = {
  id: string;
  timestamp: Date | string;
  author: { id: string; name: string };
  text: string;
};

function scrollChatMessageListToBottom(host: HTMLElement | null): void {
  if (!host) return;
  const container = host.querySelector('.dx-chat-messagelist .dx-scrollable-container');
  if (!(container instanceof HTMLElement)) return;
  container.scrollTop = container.scrollHeight;
}

export function FeedbackDiscussionChat({
  messages,
  currentUserId,
  currentUserName,
  loading = false,
  readOnly = false,
  title = 'Discussion',
  onMessageEntered,
  onMessageDeleted
}: FeedbackDiscussionChatProps) {
  const chatHostRef = useRef<HTMLDivElement>(null);

  const items = useMemo<ChatItem[]>(
    () =>
      messages.map((m) => ({
        id: m.id,
        timestamp: m.createdAt,
        author: {
          id: m.authorUserId,
          name: m.authorDisplayName?.trim() || m.authorUserId
        },
        text: m.body
      })),
    [messages]
  );

  const user = useMemo(
    () => ({
      id: currentUserId || 'unknown',
      name: currentUserName || currentUserId || 'You'
    }),
    [currentUserId, currentUserName]
  );

  useLayoutEffect(() => {
    scrollChatMessageListToBottom(chatHostRef.current);
  }, [items.length, loading]);

  const handleEntered = useCallback(
    (e: MessageEnteredEvent) => {
      const text = String(e.message?.text ?? '').trim();
      if (!text) return;
      void Promise.resolve(onMessageEntered(text)).catch((err: unknown) => {
        showErrorToast(err instanceof Error ? err.message : 'Failed to post comment.');
      });
    },
    [onMessageEntered]
  );

  const handleDeleted = useCallback(
    (e: MessageDeletedEvent) => {
      const id = String(e.message?.id ?? '');
      if (!id || !onMessageDeleted) return;
      void Promise.resolve(onMessageDeleted(id)).catch((err: unknown) => {
        showErrorToast(err instanceof Error ? err.message : 'Failed to delete comment.');
      });
    },
    [onMessageDeleted]
  );

  return (
    <div className="site-support-discussion" ref={chatHostRef}>
      {title ? <div className="site-support-discussion__title">{title}</div> : null}
      {loading ? (
        <div className="site-support-discussion__loading">
          <LoadIndicator height={24} width={24} />
        </div>
      ) : (
        <Chat
          className="site-support-discussion__chat"
          height={320}
          items={items}
          user={user}
          reloadOnChange={false}
          showAvatar
          showUserName
          showDayHeaders
          onMessageEntered={readOnly ? undefined : handleEntered}
          onMessageDeleted={readOnly || !onMessageDeleted ? undefined : handleDeleted}
          editing={
            readOnly || !onMessageDeleted
              ? undefined
              : {
                  allowUpdating: false,
                  allowDeleting: true
                }
          }
        >
          <DayHeaderFormat type="shortDate" />
          <MessageTimestampFormat type="shortTime" />
        </Chat>
      )}
    </div>
  );
}
